import dayjs from "utils/dayjs";

// Amount Conversions
export const centsToRandDisplay = function (amount) {
  const randAmount = parseFloat(amount) / 100;
  return randAmount.toFixed(2);
};

export const randToCents = (amount) => {
  const centsAmount = parseFloat(amount) * 100;
  return centsAmount.toFixed(0);
};

export const centsToRands = (amount) => {
  return amount / 100;
};

export const percentDisplay = (percent) => {
  return percent * 100;
};

export const percentReverse = (percent) => {
  return percent / 100;
};

// A whole-number share as "40%", or "—" when there is no figure to show.
export const formatPercent = (percent) =>
  Number.isFinite(percent) ? `${percent}%` : "—";

export const calculatePercent = (numerator, denominator) => {
  var percent = 0;
  if (denominator) {
    if (denominator !== 0) {
      percent = numerator / denominator;
    }
  }
  return Math.floor(percent * 100);
};

export const dateToMonthDay = function (date) {
  return dayjs(date).format("D MMM");
};

export const dateToMonthDayYear = function (date) {
  return dayjs(date).format("DD MMM YY");
};

//Date Conversions
// Every timestamp in the dashboard is read as a South African wall clock,
// so anything that renders *or filters* one has to agree on this zone.
export const DISPLAY_TIMEZONE = "Africa/Johannesburg";

// How a day is shown wherever a person reads or picks one.
export const DISPLAY_DATE_FORMAT = "DD-MM-YYYY";
// How a day travels: API date params, Firestore day ids, filter values.
export const ISO_DATE_FORMAT = "YYYY-MM-DD";

// Today's service date as the YYYY-MM-DD id the kitchen day aggregations are
// keyed by. Read in SAST, so a dashboard open in another timezone still asks
// for the day the kitchen is serving.
export const serviceDayId = () =>
  dayjs().tz(DISPLAY_TIMEZONE).format(ISO_DATE_FORMAT);

// How far the South African service day runs ahead of the viewer's clock, in
// minutes.
export const serviceDayOffsetMinutes = (now = dayjs()) =>
  now.tz(DISPLAY_TIMEZONE).utcOffset() - now.utcOffset();

// The hour the hourly aggregations were last written, on the service day's
// clock, named as such. Read locally it is a different instant in every
// half-hour zone, where `startOf("hour")` lands thirty minutes off the job's
// boundary — and a viewer eight hours behind has no way to tell which clock an
// unqualified "4:00 AM" is on.
export const lastServiceHour = () =>
  dayjs().tz(DISPLAY_TIMEZONE).startOf("hour").format("h:mm A [SAST]");

// A `YYYY-MM-DD` day in South African time as a half-open interval of epoch
// milliseconds — `[start, end)`, start being midnight and end the next
// midnight — or null for anything that isn't a day. Every timestamp renders in
// SAST, so a day picked in a filter is bounded in SAST too; bounded in UTC it
// drops the first two hours.
//
// Half-open rather than midnight-to-23:59:59.999 because both stores keep more
// precision than milliseconds — Postgres to the microsecond, Firestore to the
// nanosecond — so an inclusive upper bound drops a row written in the last
// fraction of the day. `created < next midnight` has no such gap, and needs no
// arithmetic on either end.
export const serviceDayBounds = (value) => {
  // Parsed in UTC, and strictly: `dayjs.tz` throws on a value it can't parse
  // rather than returning an invalid date, and rolls "2026-13-45" over into
  // the next year rather than rejecting it. UTC because a parse on the
  // runtime's clock accepts anything that isn't a string — a `dayjs` reaches
  // `dayjs.tz`'s instant branch and yields a part-day window — and rejects a
  // day the viewer's own calendar skipped, which is how Apia's missing
  // 30 December 2011 dropped a filter entirely.
  const day = dayjs.utc(value, ISO_DATE_FORMAT, true);
  if (!day.isValid()) {
    return null;
  }
  // Both ends are built by parsing a day string in SAST, never by moving an
  // existing instant. The timezone plugin's `startOf` and `add` round-trip
  // through the *runtime's* zone — they format the SAST wall clock, reparse
  // that string locally, floor it there, and reinterpret the result as SAST —
  // so on a day when the viewer's own zone changes offset the wall clock that
  // comes back is not the one that went in. A Sydney viewer asking for
  // 4 October 2026 was given 3 October.
  const midnight = (isoDay) =>
    dayjs.tz(isoDay, ISO_DATE_FORMAT, DISPLAY_TIMEZONE).valueOf();
  return {
    start: midnight(value),
    // `add` on a UTC dayjs, so it never touches the runtime zone either, and
    // only its `YYYY-MM-DD` is read back.
    end: midnight(day.add(1, "day").format(ISO_DATE_FORMAT)),
  };
};

// The day a `YYYY-MM-DD` names as a dayjs at local midnight, which is what a
// date picker takes, or null for anything else. Read through `new Date()` the
// string would be midnight UTC, which is the evening before anywhere west of
// Greenwich.
//
// Strictly parsed, and only from a string: a timestamp would otherwise be
// truncated to its first ten characters and rendered as the UTC calendar day
// ("2026-09-03T22:30:00Z" is 4 September in SAST), and a Date would be read on
// the viewer's clock — the off-by-a-day this exists to remove, reintroduced by
// the argument's type.
export const parseDay = (value) => {
  if (typeof value !== "string") {
    return null;
  }
  const day = dayjs(value, ISO_DATE_FORMAT, true);
  return day.isValid() ? day : null;
};

// That day for display — carrying the year, because this is what the grids'
// CSV export writes as well as what the cell shows. Blank for anything
// `parseDay` won't read.
export const formatDay = (value) =>
  parseDay(value)?.format("ddd, D MMM YYYY") ?? "";

// `formatDay` without the weekday, for a day named inside a sentence, where
// the weekday's comma reads as a clause break.
export const formatDayShort = (value) =>
  parseDay(value)?.format("D MMM YYYY") ?? "";

export const timestampToDateDisplay = function (timestamp) {
  return dayjs
    .tz(timestamp, DISPLAY_TIMEZONE)
    .format(`${DISPLAY_DATE_FORMAT} HH:mm:ss`);
};

export const dateDisplay = function (timestamp) {
  return dayjs(timestamp).format(ISO_DATE_FORMAT);
};

export const formatAmount = function (number) {
  var num = Number(number);
  var p = num.toFixed(2).split(".");
  return (
    p[0]
      .split("")
      .reverse()
      .reduce(function (acc, num, i) {
        return num === "-" ? num + acc : num + (i && !(i % 3) ? "," : "") + acc;
      }, "") +
    "." +
    p[1]
  );
};

export const formatLargeNums = function (number) {
  var num = Number(number);
  var p = num.toFixed(2).split(".");
  return p[0]
    .split("")
    .reverse()
    .reduce(function (acc, num, i) {
      return num === "-" ? acc : num + (i && !(i % 3) ? "," : "") + acc;
    }, "");
};

export const toTitleCase = function (str) {
  if (str) {
    str = str.replace(/_/g, " ");
    return str.replace(/\w\S*/g, function (txt) {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
  } else {
    return "";
  }
};

export const slugify = function (str) {
  return str
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "_") // Replace spaces with -
    .replace(/&/g, "_and_") // Replace & with 'and'
    .replace(/[^\w_]+/g, "") // Remove all non-word chars
    .replace(/__+/g, "_");
};

export const capitalizeFirstLetter = function (string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

// Title-case a stored name for display, treating hyphens and underscores as
// word separators: "hot-breakfast" and "hot_breakfast" both render as
// "Hot Breakfast". The rest of each word is left as stored so acronyms such as
// "BBQ" survive.
export const capitalizeWords = function (str) {
  if (!str) return str;
  return String(str)
    .trim()
    .split(/[\s_-]+/)
    .map((word) => capitalizeFirstLetter(word))
    .join(" ");
};

// Category names are stored slug-like ("hot-breakfast"); a copy for display.
export const withTitleCasedName = (row) => ({
  ...row,
  name: capitalizeWords(row.name),
});

// How a residence is named wherever one is picked or listed.
export const residenceLabel = (residence) =>
  `${residence.building_id} - ${residence.description}`;

// The same from an id and a Map of the residences by id, or "#id" for one the
// Map doesn't hold.
export const residenceLabelById = (residenceById, id) => {
  const residence = residenceById.get(id);
  return residence ? residenceLabel(residence) : `#${id}`;
};

export const sortArrayBy = function (field, reverse, primer) {
  var key = function (x) {
    return primer ? primer(x[field]) : x[field];
  };

  reverse = !reverse ? 1 : -1;

  return function (a, b) {
    a = key(a);
    b = key(b);
    return reverse * (a < b ? -1 : a > b ? 1 : 0);
  };
};
